/* eslint-disable @typescript-eslint/no-explicit-any */
import { FC, HTMLAttributeAnchorTarget } from 'react';
import { LOCALES_CONFIG } from '@global/App/constants/location/constants';
import { ShippingAddress } from '@src/pages/checkout/types';
import { SiteId } from 'wly-ecommerce-common';

declare global {
  interface Window {
    digitalData: any;
    adobeDataLayer: any;
    Osano: any;
    turnstile: {
      reset: () => void;
    };
  }
}

export enum SubscriptionStatus {
  Created = 'Created',
  Placed = 'Placed',
  Paid = 'Paid',
  Fulfilled = 'Fulfilled',
  Active = 'Active',
  Cancelling = 'Cancelling',
  Cancelled = 'Cancelled',
}

export enum B2CSubscriptionStatus {
  CREATED = 'CREATED',
  ACTIVE = 'ACTIVE',
  PENDING_RENEWAL = 'PENDING_RENEWAL',
  PAYMENT_FAILED = 'PAYMENT_FAILED',
  PENDING_CANCELLATION = 'PENDING_CANCELLATION',
  CANCELLED = 'CANCELLED',
}

export type AllSubscriptionStatus = SubscriptionStatus | B2CSubscriptionStatus;

export type SubscriptionSiteId =
  | SiteId.COD
  | SiteId.AGS
  | SiteId.VET
  | SiteId.SPECTRABASE
  | SiteId.AIGATEWAY;

export type EdgesType<CODE = string> = {
  mapsOn: {
    code: CODE;
  };
  node: {
    amount?: number;
    paId?: string;
    units?: {
      code: string;
    };
  };
};

export interface PricPrice {
  edges: EdgesType[];
}

export type NavEntityType = {
  to: string;
  isInAppNav?: boolean;
  label: string;
  target?: HTMLAttributeAnchorTarget;
};

// interfaces
export interface IBillingInfo {
  name?: string;
  tel?: string;
  addressOne: string;
  addressTwo: string;
  addressThree?: string;
  city: string;
  state: string;
  zip: string;
  country: {
    name: string;
    numericCode?: string;
    iso2Code?: string;
  };
  uid?: string;
  wNewAddress?: boolean;
}

export enum DigitalFormat {
  DIGITAL = 'DIGITAL',
  PHYSICAL = 'PHYSICAL',
  DIGITAL_AND_PHYSICAL = 'DIGITAL_AND_PHYSICAL',
}

export type PaymentMethodType = 'Card';

export type PaymentInfo = {
  nameOnCard: string;
  cardNumber: string | number;
  expirationDate: string | number;
  wPaymentType?: string;
  orPlacementDate?: string;
};

export type SubscriptionProduct = {
  maId: string;
  maName: string;
  imageUrl: string;
};

interface ProductAuthor {
  wName: string;
}

interface ViaxAttachment {
  atUrl: string;
  wFormat: 'normal' | 'thumbnail';
}

interface ParentCategory {
  catId: string;
  catName: string;
  wCatType: string;
}

export interface IProductParent {
  node: {
    maId: string;
    maName?: string;
    wAuthors?: ProductAuthor[];
    wImages?: ViaxAttachment[];
    wTextbook?: boolean;
    maCategories?: ParentCategory[];
  };
}

export interface LicenseAgreementInfo {
  wplaiName: string;
  wplaiUrl: string;
}

export interface IProduct {
  description?: string;
  imageUrl?: string;
  maName: string | null;
  pricPrice: PricPrice;
  maId: string;
  uid: string;
  wBookTotalPages?: number;
  wIsbn?: string;
  wEditionFormat?: string;
  wPrintOnDemand?: boolean;
  __typename?: string;
  parents?: IProductParent[];
  wIsbn13: string;
  wNumberOfPages: string;
  wPublicationDate: string;
  maCategories: { catName: string; catId: string }[];
  wProductLicenseAgreementInfo?: LicenseAgreementInfo;
}

export interface PaybBillingPlanFrequency {
  ofUnit: 'MONTH' | 'YEAR';
}

export interface PaybBillingPlan {
  bipBillAtRPIT: PaybBillingPlanFrequency;
  auAudit: {
    createdAt: {
      dateTime: string;
    };
  };
}

export interface hiConsistsOfCustomerOrderBiId {
  biiId: string;
  biiQuantity: {
    amount: number;
  };
  pricPrice: PricPrice;
  biiSalable: IProduct;
  biSequentialId: string;
  subsActivationDate?: string;
  paybBillingPlan?: PaybBillingPlan;
  __typename: string;
}

export interface IPaybPayment {
  ccpaCardNumber: null | string;
  ccpaExpirationDate: null | number | string;
  ccpaNameOnCard: null | string;
  wPaymentType: PaymentMethodType;
}

export interface IGadCountry {
  name?: string;
  uid?: string;
  numericCode?: string;
  iso2Code?: string;
}

export enum TypesName {
  ViaxTrialPromotion = 'ViaxTrialPromotion',
}

export interface ICorPromoCodes {
  id?: string;
  uid?: string;
  prmName?: string;
  typeName?: TypesName;
  prmId?: string;
  trpDuration?: ITRPDuration;
  wFreeStandardShipping?: boolean;
}

export interface ITRPDuration {
  amount?: string;
  units?: string;
}

export interface IPaybBillingAddress {
  id: string;
  uid: string;
  typeName: string;
  wCountry?: null | string;
  gadCountry: IGadCountry;
  gadStateOrProvince: string;
  upaLine1: string;
  upaLine2: string;
  upaPostcode: string;
  upaCity: string;
}

export interface BiRelatesToEdges {
  node: {
    uid: string;
    typeName: string;
    createdAt: string;
    tadDeliveryAddress: ShippingAddress;
  };
}

export interface BiRelatesTo {
  totalCount: number;
  edges: BiRelatesToEdges[];
}

export interface IOrder {
  biCreatedAt: string;
  uid: string;
  orPlacementDate?: string;
  biId?: string;
  hiConsistsOf: hiConsistsOfCustomerOrderBiId[];
  id: string;
  corPromoCodes?: ICorPromoCodes[];
  paybBillingAddress?: null | IPaybBillingAddress;
  paybPayment?: IPaybPayment;
  pricPrice: PricPrice;

  prRelatedParties?: {
    edges: EdgesType[];
  };

  biRelatesTo?: BiRelatesTo;
  wSiteId?: SiteId;
  wSchoolFiceCode?: string;
  wContactPhone?: null | string;
  wTOSAccepted?: null | boolean;
  wGuestOrder?: boolean;

  wSelectedDeliveryOption?: { wdoCode: string };
  lastBillBiId?: string | null;
  billFromOrder?: BillInformation;
}

export interface BillInformation {
  edges: {
    node: {
      biId: string;
      billDate: string;
      billFinancialCharges: {
        fichFinancialTransaction: {
          fitrStatus: {
            code: string;
          };
        };
      }[];
    };
  }[];
}

export type LOCALE_CODES = keyof typeof LOCALES_CONFIG;

export interface Region {
  id: number;
  name: string;
  locale: string;
  status: string;
  isoCodeLong?: string;
  isoCodeShort?: string;
}

export interface Country {
  id: number;
  name: string;
  locale: string;
  region: string;
  currency: string;
  fallbackCountry: string;
  isoCodeTwo: string;
  isoCodeThree: string;
  numCode: number;
  status: string;

  regions: Region[] | null;
}

export interface User {
  acr: string;
  azp: string;
  email: string;
  email_verified: boolean;
  exp: number;
  family_name: string;
  given_name: string;
  iat: number;
  iss: string;
  jti: string;
  name: string;
  preferred_username: string;
  realm: string;
  realm_access: {
    roles: string[];
  };
  scope: string;
  session_state: string;
  sid: string;
  sub: string;
  typ: string;
  uid: string;
}

export interface IPayment {
  paymentType: string;
  payment: {
    token: string;
    transactionId: string;
    returnCode: string;
    returnMessage: string;
    timestamp: string;
    currency: string;
  };
}

export type MappedPaymentComponentsType = Record<PaymentMethodType, FC>;

export type Price = {
  value: number;
  code: string;
  units?: string;
};

export enum PRODUCT_AVAILABILITY_RULES {
  RentalInventory = 'product-rental-inventory',
  PurchaseOptions = 'product-purchase-options',
  PriceNotPresent = 'product-price-not-present',
  RestrictedCountries = 'product-restricted-countries',
  DiscontinuedOrPreviewStatus = 'product-discontinued-or-preview-status',
}

export type ResponseStatus = 'OK' | 'ERROR';

export enum DiscountValueType {
  AMOUNT = 'AMOUNT',
  PERCENTAGE = 'PERCENTAGE',
}

export enum DiscountType {
  COUPON_DISCOUNT = 'COUPON-DISCOUNT',
  TARGET_PRICE_DISCOUNT = 'TARGET-PRICE-DISCOUNT',
  ADD_DISCOUNT = 'ADD-DISCOUNT',
  SITEWIDE_PRODUCT_DISCOUNT = 'SITEWIDE-PRODUCT-DISCOUNT',
  SITEWIDE_SITE_ID_DISCOUNT = 'SITEWIDE-SITE-ID-DISCOUNT',
  FREETRIAL = 'FREETRIAL',
}

export enum TrialInterval {
  DAY = 'DAY',
  MONTH = 'MONTH',
}

interface TrialDuration {
  amount: number;
  units: TrialInterval;
}

export interface OrderItemDiscounts {
  itemNumber: string;
  valueType: DiscountValueType;
  discountAmount: number;
  discountValue: number;
  discountType: DiscountType;
  freeTrialDuration: TrialDuration;
}

export interface Discount {
  itemNumber: string;
  title: string;
  discount: string;
  freeTrialDuration?: TrialDuration;
}

export interface Address {
  uid: string;
  wCountry: string;
  gadStateOrProvince: string;
  upaCity: string;
  upaPostcode: string;
  upaLine1: string;
  upaLine2: string;
  wLine3: string;
  wFirstName: string;
  wLastName: string;
  upaPhoneNumber: string;
}

export enum AddressType {
  'SHIPPING' = 'SHIPPING',
  'BILLING' = 'BILLING',
}

interface ArrivalEstimation {
  from: string;
  to: string;
}

interface DeliveryCost {
  currencyCode: string;
  price: number;
}

interface DeliveryOptionData {
  wSequenceNumber: number;
  wdoCode: string;
  wdoDMCode: string;
  wdoCountry: string;
  wdoName: string;
  wdoDescription: string;
  wdoMinDeliveryDays: number;
  wdoMaxDeliveryDays: number;
  wdoFullPriceReduce: boolean;
  wdoDeliveryRangeExtension: boolean;
  wdoFirstUnitCost: number;
  wdoSubsequentUnitsCost: number;
}

export interface DeliveryOption {
  deliveryOption: DeliveryOptionData;
  deliveryCost: DeliveryCost;
  arrivalEstimation: ArrivalEstimation;
}
